import React from 'react';
import { Mail } from 'lucide-react';
import { examCatalog, ExamCatalogEntry } from '../data/examCatalog';
import { PaymentLogos } from './PaymentLogos';
import { PrePraxisBadge } from './brand/PrePraxisBadge';
import { UserProfile, AuthIntent } from '../types';

interface FooterProps {
  setActiveTab: (tab: string) => void;
  onSelectExam?: (entry: ExamCatalogEntry) => void;
  onOpenPricingModal: () => void;
  onOpenAuthModal: (mode?: 'signin' | 'signup', intent?: AuthIntent) => void;
  userProfile?: UserProfile;
}

export const Footer: React.FC<FooterProps> = ({
  setActiveTab,
  onSelectExam,
  onOpenPricingModal,
  onOpenAuthModal,
  userProfile
}) => {
  const isLoggedIn = Boolean(userProfile?.uid && userProfile.uid !== 'guest_user_1');

  const handleExamClick = (exam: ExamCatalogEntry) => {
    if (!isLoggedIn) {
      onOpenAuthModal('signup', { actionType: 'exam', exam, contextTitle: exam.shortLabel });
    } else {
      if (onSelectExam) {
        onSelectExam(exam);
      }
      setActiveTab('papers');
    }
  };

  const handleTabClick = (tabId: string, authIntent?: { actionType: AuthIntent['actionType']; tab?: string; contextTitle?: string }) => {
    if (!isLoggedIn && authIntent) {
      onOpenAuthModal('signup', authIntent);
    } else {
      setActiveTab(tabId);
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-400 border-t border-slate-800 text-xs mt-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* Main Grid: 4 Comprehensive Columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          
          {/* Col 1: Brand, Mission & Support Contact */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="bg-indigo-600 text-white w-7 h-7 rounded-lg p-0.5 flex items-center justify-center shrink-0 shadow-xs">
                <PrePraxisBadge className="w-5 h-5 text-white" showLaurels={true} showCap={true} />
              </div>
              <span className="font-bold text-white text-base">Pre-Praxis PREP</span>
            </div>
            <p className="text-slate-400 text-xs leading-relaxed">
              Standardized educator certification exam preparation, diagnostic simulations, and official competency alignment across state and national testing frameworks.
            </p>
            <div className="pt-1 text-xs font-semibold text-slate-300">
              <a 
                href="mailto:support@prepraxis.com" 
                className="inline-flex items-center gap-1.5 text-slate-300 hover:text-indigo-400 transition"
              >
                <Mail className="w-3.5 h-3.5 text-indigo-400" />
                <span>support@prepraxis.com</span>
              </a>
            </div>
          </div>

          {/* Col 2: Full Exam Families (Complete Catalog) */}
          <div>
            <h4 className="text-slate-200 font-bold uppercase text-xs tracking-wider mb-3">Exam Families</h4>
            <ul className="space-y-1.5 text-xs">
              {examCatalog.map((exam) => (
                <li key={exam.id}>
                  <button
                    onClick={() => handleExamClick(exam)}
                    className="hover:text-indigo-400 transition cursor-pointer text-left flex items-center gap-1"
                  >
                    <span className="font-semibold text-slate-300">{exam.shortLabel}</span>
                    <span className="text-slate-500">({exam.state})</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3: Platform Resources & Navigation */}
          <div>
            <h4 className="text-slate-200 font-bold uppercase text-xs tracking-wider mb-3">Platform Resources</h4>
            <ul className="space-y-2 text-xs">
              <li>
                <button 
                  onClick={() => setActiveTab('landing')} 
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Home
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleTabClick('papers', { actionType: 'exam', tab: 'papers', contextTitle: 'Exams Directory' })} 
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Exams Catalog ({examCatalog.length} Families)
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleTabClick('mock-exams', { actionType: 'exam', tab: 'mock-exams', contextTitle: 'Mock Exams' })} 
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Mock Exam Simulator
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleTabClick('guides', { actionType: 'guides', tab: 'guides', contextTitle: 'Study Guides' })} 
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Study Guides & Blueprints
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveTab('blog')}
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Blog
                </button>
              </li>
              <li>
                <button 
                  onClick={() => setActiveTab('faq')} 
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Frequently Asked Questions
                </button>
              </li>
              <li>
                <button 
                  onClick={onOpenPricingModal} 
                  className="hover:text-indigo-400 transition cursor-pointer font-bold text-indigo-400"
                >
                  All-Access Pricing ($35/mo)
                </button>
              </li>
              <li>
                <button 
                  onClick={() => handleTabClick('forum', { actionType: 'community', tab: 'forum', contextTitle: 'Community' })} 
                  className="hover:text-indigo-400 transition cursor-pointer"
                >
                  Educator Community
                </button>
              </li>
            </ul>
          </div>

          {/* Col 4: Secure Payments & Accreditation Notice */}
          <div className="space-y-4">
            <div>
              <h4 className="text-slate-200 font-bold uppercase text-xs tracking-wider mb-2">Secure Payments</h4>
              <p className="text-slate-400 text-xs leading-relaxed mb-2.5">
                Encrypted checkout with Visa, Mastercard, Verve, and Amex via Paystack.
              </p>
              {/* Preserved Payment Logos & Paystack Badge */}
              <div className="pt-0.5">
                <PaymentLogos />
              </div>
            </div>

            <div className="pt-2 border-t border-slate-800">
              <h4 className="text-slate-200 font-bold uppercase text-xs tracking-wider mb-1.5">Accreditation Notice</h4>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                PrePraxis Prep is an independent test prep provider and is not affiliated with or endorsed by CTC, ETS, TEA, Pearson, or state licensing boards. All trademarks belong to their respective owners.
              </p>
            </div>
          </div>

        </div>

        {/* Bottom Legal & Copyright Line */}
        <div className="pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px]">
          <div className="flex flex-wrap items-center gap-3 text-slate-400">
            <span>© {new Date().getFullYear()} PrePraxis Prep Educator Services. All rights reserved.</span>
            <span>•</span>
            <span>Build 4.8.74</span>
            <span>•</span>
            <span className="text-slate-500">All 8 State & National Exam Frameworks Loaded</span>
          </div>

          <div className="flex items-center gap-3 text-slate-400 font-medium">
            <button onClick={() => setActiveTab('faq')} className="hover:text-indigo-400 transition cursor-pointer">Terms of Service</button>
            <span>•</span>
            <button onClick={() => setActiveTab('faq')} className="hover:text-indigo-400 transition cursor-pointer">Privacy Policy</button>
            <span>•</span>
            <button onClick={() => setActiveTab('faq')} className="hover:text-indigo-400 transition cursor-pointer">Refund Policy</button>
          </div>
        </div>

      </div>
    </footer>
  );
};
